// Node status: 0 = Offline, 1 = Online, 2 = Standby
export const NodeStatus = {
  Offline: 0,
  Online: 1,
  Standby: 2,
} as const

// Hardware status: 0 = Available, 1 = Occupied, 2 = Excluded, 3 = Maintaining
export const HardwareStatus = {
  Available: 0,
  Occupied: 1,
  Excluded: 2,
  Maintaining: 3,
} as const

// Get node status color class
export function getNodeStatusColor(status: number): string {
  switch (status) {
    case NodeStatus.Online:
      return 'text-green-500'
    case NodeStatus.Standby:
      return 'text-neutral-600'
    default:
      return 'text-red-500'
  }
}

// Get node status i18n key
export function getNodeStatusTextKey(status: number): string {
  switch (status) {
    case NodeStatus.Online:
      return 'common.online'
    case NodeStatus.Standby:
      return 'common.standby'
    default:
      return 'common.offline'
  }
}

// Get hardware status color class
export function getHardwareStatusColor(status: number): string {
  switch (status) {
    case HardwareStatus.Available:
      return 'text-green-500'
    case HardwareStatus.Occupied:
      return 'text-orange-500'
    default:
      return 'text-secondary'
  }
}

// Get hardware status i18n key
export function getHardwareStatusTextKey(status: number): string {
  switch (status) {
    case HardwareStatus.Available:
      return 'nodeStatus.available'
    case HardwareStatus.Occupied:
      return 'nodeStatus.occupied'
    case HardwareStatus.Excluded:
      return 'nodeStatus.excluded'
    case HardwareStatus.Maintaining:
      return 'nodeStatus.maintaining'
    default:
      return 'nodeStatus.unknown'
  }
}

// Calculate percentage with zero-division safety
export function getPercentage(allocated: number, total: number): string {
  if (total === 0) return '0.00'
  return ((allocated / total) * 100).toFixed(2)
}

export interface AllocationDisplay {
  value: string
  subValue?: string
}

// Format CPU allocation display
export function formatCpuAllocation(allocated: number, total?: number): AllocationDisplay {
  if (total) {
    const percent = getPercentage(allocated, total)
    return { value: `${allocated} / ${total} Cores`, subValue: `(${percent}%)` }
  }
  return { value: `${allocated} Cores`, subValue: undefined }
}

// Format memory allocation display (input in MB)
export function formatMemoryAllocation(allocatedMB: number, totalMB?: number): AllocationDisplay {
  const allocatedGB = Math.round(allocatedMB / 1024)
  if (totalMB) {
    const totalGB = Math.round(totalMB / 1024)
    const percent = getPercentage(allocatedMB, totalMB)
    return { value: `${allocatedGB} / ${totalGB} GB`, subValue: `(${percent}%)` }
  }
  return { value: `${allocatedGB} GB`, subValue: undefined }
}

// Format storage value with appropriate unit (GB or TB)
function formatStorageValue(gb: number): string {
  if (gb >= 1024) {
    return `${(gb / 1024).toFixed(1)} TB`
  }
  return `${gb} GB`
}

// Format storage allocation display (input in GB)
export function formatStorageAllocation(allocatedGB: number, totalGB?: number): AllocationDisplay {
  if (totalGB) {
    const percent = getPercentage(allocatedGB, totalGB)
    return {
      value: `${formatStorageValue(allocatedGB)} / ${formatStorageValue(totalGB)}`,
      subValue: `(${percent}%)`,
    }
  }
  return { value: formatStorageValue(allocatedGB), subValue: undefined }
}
